import { useAuth } from 'auth/context/AuthContext';
import { useCallback, useMemo } from 'react';

import { areAllKeysDefined, ObjectValidate } from 'utilities/functions';
import {
  FHIR_USER_FIELDS_REQUIRED_FOR_PERSONAL_DETAILS_COMPLETE,
  GOOGLE_EHR_AVAILABLE_SYSTEMS_IDENTIFIERS,
  USER_FIELDS_REQUIRED_FOR_PERSONAL_DETAILS_COMPLETE,
} from 'utilities/constants';
import {
  FhirQuestionnaireNames,
  useGetFhirPatientbyCodexIdLazyQuery,
  useGetFhirQuestionnaireResponseLazyQuery,
} from 'graphql/generated/remote-schema-hasura';
import {
  Persona_Inquiry_Status_Enum,
  QuestionnaireResponseStatus,
  useGetAgreementAgeOfMajorityByUserIdLazyQuery,
  useGetAgreementSubstanceByUserIdLazyQuery,
  useGetAgreementSudRecordsByUserIdLazyQuery,
  useGetPersonaVerificationStatusByEmailLazyQuery,
} from 'graphql/generated/hasura';
import { Roles } from '../firebase/interfaces';
import { useGetUserVerifyStatuses } from './useGetUserVerifyStatuses';

const useUserValidation = (includePersona = true) => {
  const { user: loggedUser, selectedAccount } = useAuth();

  const { userVerficationData, loading: userVerificationSubLoading } =
    useGetUserVerifyStatuses();

  const kyc_current_status = useMemo(
    () => userVerficationData?.users?.[0]?.kyc_current_status,
    [userVerficationData],
  );

  const [
    getPersonaVerificationStatusByEmail,
    { loading: userVerificationLoading, error: userVerificationError },
  ] = useGetPersonaVerificationStatusByEmailLazyQuery({
    context: {
      ignoreGlobalErrors: true,
    },
    fetchPolicy: 'network-only',
  });

  const [fetchFhirUser, { error: errorFhirUser, loading: fhirLoading }] =
    useGetFhirPatientbyCodexIdLazyQuery({
      onError: (e) => console.error(e),
      context: {
        ignoreGlobalErrors: true,
      },
      fetchPolicy: 'network-only',
    });

  const [fetchFhirQuestionnaire, { error, loading: loadingFhirQuestionnaire }] =
    useGetFhirQuestionnaireResponseLazyQuery({
      context: {
        ignoreGlobalErrors: true,
      },
      fetchPolicy: 'network-only',
    });

  const [getAgreement] = useGetAgreementAgeOfMajorityByUserIdLazyQuery({
    context: {
      ignoreGlobalErrors: true,
    },
  });

  const [getSudAgreement] = useGetAgreementSudRecordsByUserIdLazyQuery({
    context: {
      ignoreGlobalErrors: true,
    },
  });

  const [getSubstanceAgreement] = useGetAgreementSubstanceByUserIdLazyQuery({
    context: {
      ignoreGlobalErrors: true,
    },
    variables: {
      id: loggedUser?.uuid,
    },
  });

  const userValidation = useCallback(async () => {
    let isPersonalInfoValid = false,
      isContactInfoValid = false,
      isStateDobValid = false,
      isSudReport = false,
      isSubstance = false,
      isQuestionnaireValid = false;

    const activeAccountId = selectedAccount || loggedUser?.uuid || '';

    if (loggedUser?.role === Roles.PATIENT) {
      const userResponsePromise = fetchFhirUser();

      const agreementPromise = getAgreement({
        variables: { id: activeAccountId },
      });
      const sudAgreementPromise = getSudAgreement({
        variables: { id: activeAccountId },
      });
      const substanceAgreementPromise = getSubstanceAgreement();

      const [
        userResponse,
        { data: agreement },
        { data: sudAgreement },
        { data: substanceAgreement },
      ] = await Promise.all([
        userResponsePromise,
        agreementPromise,
        sudAgreementPromise,
        substanceAgreementPromise,
      ]);

      const user = userResponse.data?.getFHIRPatientbyCodexID?.users[0];
      isSudReport =
        sudAgreement?.user_agreements?.[0]?.status ===
        Persona_Inquiry_Status_Enum.Approved;

      isSubstance =
        substanceAgreement?.user_agreements?.[0]?.status ===
        Persona_Inquiry_Status_Enum.Approved;
      isStateDobValid =
        user?.SENSITIVE_dob &&
        user?.SENSITIVE_address_information?.state &&
        !selectedAccount
          ? agreement?.user_agreements?.[0]?.status ===
              Persona_Inquiry_Status_Enum.Approved || user?.is_state_dob_valid
          : true;
      isPersonalInfoValid = areAllKeysDefined(
        user as unknown as ObjectValidate,
        USER_FIELDS_REQUIRED_FOR_PERSONAL_DETAILS_COMPLETE,
      );
      isContactInfoValid = areAllKeysDefined(
        user as unknown as ObjectValidate,
        FHIR_USER_FIELDS_REQUIRED_FOR_PERSONAL_DETAILS_COMPLETE,
      );
      const questionnaire = await fetchFhirQuestionnaire({
        variables: {
          codexPatientId: selectedAccount || loggedUser?.uuid || '',
          identifier: FhirQuestionnaireNames.HealthQuestionnaire,
          system: GOOGLE_EHR_AVAILABLE_SYSTEMS_IDENTIFIERS.codex,
        },
      });

      isQuestionnaireValid = Boolean(
        questionnaire.data?.getFHIRQuestionnaireResponse.questionnaire_response
          .responses[0] &&
          questionnaire.data?.getFHIRQuestionnaireResponse
            .questionnaire_response.is_complete ===
            QuestionnaireResponseStatus.Completed,
      );
    }

    let isPersonaVerified = !includePersona;
    if (includePersona) {
      const verificationStatusQueryResult =
        await getPersonaVerificationStatusByEmail({
          variables: {
            email: loggedUser?.email || '',
          },
        });

      isPersonaVerified =
        verificationStatusQueryResult.data?.users?.[0]?.kyc_current_status ===
          Persona_Inquiry_Status_Enum.Approved || !!kyc_current_status;
    }

    return {
      isPersonalInfoValid,
      isContactInfoValid,
      isQuestionnaireValid,
      isPersonaVerified,
      isStateDobValid,
      isSudReport,
      isSubstance,
      generalValidation:
        isPersonalInfoValid &&
        isContactInfoValid &&
        isQuestionnaireValid &&
        isSudReport &&
        isSubstance &&
        isStateDobValid &&
        (isPersonaVerified || !includePersona),
    };
  }, [
    fetchFhirQuestionnaire,
    fetchFhirUser,
    getAgreement,
    getSudAgreement,
    getSubstanceAgreement,
    getPersonaVerificationStatusByEmail,
    includePersona,
    loggedUser,
    kyc_current_status,
    selectedAccount,
  ]);

  return {
    userValidation,
    loading:
      loadingFhirQuestionnaire ||
      fhirLoading ||
      userVerificationLoading ||
      userVerificationSubLoading,
    error: error || errorFhirUser || userVerificationError,
  };
};

export default useUserValidation;
